import { Controller } from '@hotwired/stimulus'
import { put } from '@rails/request.js'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import MobileDetect from 'mobile-detect'

// Connects to data-controller="driver"
export default class extends Controller {
  connect() {
    if (this.element.dataset.driverViewed === 'true') {
      return
    }

    const md = new MobileDetect(window.navigator.userAgent)
    const mobile = Boolean(md.mobile())

    this.driverObj = driver({
      showProgress: true,
      steps: mobile ? this.mobileSteps() : this.desktopSteps()
    })

    if (mobile && this.organizationMode()) {
      this.openDrawer(() => this.driverObj.drive())
    } else {
      this.driverObj.drive()
    }

    this.updateDriverViewed()
  }

  mobileSteps() {
    const steps = [
      {
        element: '.dialer',
        popover: {
          title: 'Tap to Start Calling',
          description: 'Use the dialer to make a call. You can enter a number manually or select a contact to get started.'
        }
      },
      {
        element: '.mobile-menu-button',
        popover: {
          title: 'Find Everything Here',
          description: 'Tap the menu to reach your team, numbers, billing, and account settings.'
        }
      }
    ]

    if (this.organizationMode()) {
      steps.unshift({
        element: '[data-sidebar-target="mobile"] .mode-toggle-link',
        popover: this.modeTogglePopover(),
        onHighlightStarted: () => this.openDrawer(() => this.driverObj.refresh()),
        onDeselected: () => this.sidebarController.close()
      })
    }

    return steps
  }

  desktopSteps() {
    const steps = [
      {
        element: '.dialer',
        popover: {
          title: 'Start a Call Instantly',
          description: 'This is your dialer. Enter a phone number or select a saved contact to begin a call right from your browser.'
        }
      },
      {
        element: '.sidebar-desktop .power-dialer-link',
        popover: {
          title: 'Work Your Call List',
          description: 'The Power Dialer runs through your campaigns for you — it dials each contact, you log the outcome, and it lines up the next call.'
        }
      },
      this.organizationStep(),
      {
        element: '.sidebar-desktop .buy-numbers-link',
        popover: {
          title: 'Get Your Own Number',
          description: 'Purchase a private U.S. or Canadian number to make calls from your own line, not just the public ZenCall number.'
        }
      },
      {
        element: '.sidebar-desktop .credits-link',
        popover: {
          title: 'Manage Your Credit',
          description: 'ZenCall is pay-as-you-go. Add call credit here securely — no subscriptions, no hidden fees.'
        }
      },
      {
        element: '.sidebar-desktop .profiles-link',
        popover: {
          title: 'Your Profile & Settings',
          description: 'Update your profile, manage your account, and tweak your calling preferences in one place.'
        }
      }
    ]

    if (this.organizationMode()) {
      steps.unshift({
        element: '.sidebar-desktop .mode-toggle-link',
        popover: this.modeTogglePopover()
      })
    }

    return steps
  }

  organizationStep() {
    if (this.element.dataset.driverOrganization === 'true') {
      return {
        element: '.sidebar-desktop .organization-link',
        popover: {
          title: 'Invite Your Team',
          description: 'Manage your organization and invite teammates here — everyone shares one credit pool.'
        }
      }
    }

    return {
      element: '.sidebar-desktop .create-organization-link',
      popover: {
        title: 'Create an Organization',
        description: 'Working with a team? Create an organization to invite teammates and share credits.'
      }
    }
  }

  modeTogglePopover() {
    return {
      title: 'You\'re in Organization Mode',
      description: 'You\'re working in your organization\'s shared workspace. Use this switcher anytime to flip to your personal workspace, which has its own numbers, contacts, and credits.'
    }
  }

  organizationMode() {
    return this.element.dataset.driverOrganization === 'true'
  }

  openDrawer(onOpened) {
    const drawer = this.element.querySelector('[data-sidebar-target="mobile"]')

    if (drawer.classList.contains('translate-x-0')) {
      onOpened()
      return
    }

    drawer.addEventListener('transitionend', onOpened, { once: true })
    this.sidebarController.open()
  }

  get sidebarController() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'sidebar')
  }

  async updateDriverViewed() {
    await put('/driver_viewed', {
      body: {
        driver_viewed: true
      },
      responseKind: 'json'
    })
  }
}
